/**
 * The Kraken's phase machine (plan E8, reordered to Adam's finale redesign 2026-07-30).
 *
 * Pure simulation, like everything in this directory: no engine, no DOM, no wall clock,
 * all randomness through the seeded Rng. The phases are a state machine rather than a
 * timeline on purpose — a boss that advances on a clock is a cutscene, and the whole
 * point of E8 is that each phase ends because of something the player did.
 *
 * The order tells the story Adam asked for: the creature arrives hidden, fights at range
 * with its body — torpedoes while it is up, depth charges while it is down — and only
 * once that fight is lost does it stop keeping its distance and come for the platform
 * itself. Limbs rear out of the water and hook the deck edge; severing them all brings
 * the head up a few metres from the stance, eye level with the deck, and the fight ends
 * the way the whole game has been teaching: rounds, exactly where they matter.
 */

import type { Vessel } from './types';
import type { Rng } from './rng';
import { KRAKEN } from '../content/boss';
import { vesselSpec, SUBMERGED_DEPTHS } from '../content/vessels';
import { krakenEyePoint, krakenStareHeading } from '../content/creatureScale';

export type KrakenPhase =
  | 'dread'
  | 'mantle'
  | 'submerged'
  | 'tentacles'
  | 'rise'
  | 'dying'
  | 'gone';

export interface KrakenState {
  phase: KrakenPhase;
  /** Seconds spent in the current phase. */
  timer: number;
  /** Torpedo hits landed on the mantle during the current surfaced phase. */
  mantleHits: number;
  /** Depth-charge detonations close enough to count during the current dive. */
  chargeHits: number;
  /** Completed mantle/submerged cycles. */
  cycle: number;
  /** True while the eye is exposed and the autocannon can hurt it. */
  eyeExposed: boolean;
  /** Rounds the player has put into the staring eye. */
  eyeHits: number;
  /** Seconds until the next wave slam while surfaced. */
  slamTimer: number;
  /** Set for one tick when a slam lands, so the world can charge integrity for it. */
  slammed: boolean;
  /** Set for one tick when the phase changed, so the world can narrate it. */
  justChanged: boolean;
  /** Tentacles this encounter still expects to spawn. */
  tentaclesToSpawn: number;
}

export function createKrakenState(): KrakenState {
  return {
    phase: 'dread',
    timer: 0,
    mantleHits: 0,
    chargeHits: 0,
    cycle: 0,
    eyeExposed: false,
    eyeHits: 0,
    slamTimer: KRAKEN.slamIntervalSeconds,
    slammed: false,
    justChanged: false,
    tentaclesToSpawn: 0,
  };
}

function enter(state: KrakenState, phase: KrakenPhase): void {
  state.phase = phase;
  state.timer = 0;
  state.justChanged = true;
  state.mantleHits = 0;
  state.chargeHits = 0;
  state.eyeExposed = false;
  state.slamTimer = KRAKEN.slamIntervalSeconds;
}

/** Shortest-arc turn, bounded by a rate — the machine's own steering for the stare. */
function turnToward(current: number, want: number, maxDelta: number): number {
  let delta = (want - current) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return current + Math.max(-maxDelta, Math.min(maxDelta, delta));
}

/**
 * Advance the encounter one tick.
 *
 * `boss` is the Kraken vessel itself, or null during P0 when there is deliberately
 * nothing in the water to shoot at. `tentaclesAlive` closes the assault — the phase ends
 * when the player has cut them all off, not when a timer says so.
 */
export function stepKraken(
  state: KrakenState,
  boss: Vessel | null,
  tentaclesAlive: number,
  rng: Rng,
  dt: number,
): void {
  state.timer += dt;
  state.justChanged = false;
  state.slammed = false;

  /*
   * If the creature is gone from the world while the fight is still running, end the
   * encounter rather than waiting on a vessel that no longer exists. The phases below
   * bail out when `boss` is null, which once left the machine stalled in `submerged`
   * forever — the shift could then only end on its watchdog.
   */
  if (!boss && state.phase !== 'dread' && state.phase !== 'gone') {
    enter(state, 'gone');
    return;
  }

  // The eye is only ever a target during the stare; everywhere else the field is clear.
  if (boss && state.phase !== 'rise') boss.eyeTarget = undefined;

  /*
   * The dive and the surfacing, as a body rather than a switch.
   *
   * Ramped here, after the phases below have set `submerged` for this tick, so the
   * animation always chases the gameplay truth rather than leading it. Deterministic:
   * one rate, driven by dt.
   */
  const rampSubmergence = (): void => {
    if (!boss) return;
    const want = boss.submerged ? 1 : 0;
    const step = dt / KRAKEN.diveSeconds;
    boss.submergence = want > boss.submergence
      ? Math.min(want, boss.submergence + step)
      : Math.max(want, boss.submergence - step);
  };

  switch (state.phase) {
    case 'dread': {
      /*
       * Nothing to do and nothing to shoot. The sonar screams, radar ghosts appear and
       * vanish, and a bulge circles closer. When it ends, the creature surfaces — "it is
       * up, that is the whole thing" — and the range fight begins.
       *
       * It has to be *under*, and it once was not: `createVessel` only submerges
       * mini-subs and this case never set it, so the creature surfaced the instant it
       * spawned and the reveal happened before the reveal.
       */
      if (boss) { boss.submerged = true; boss.submergedDepth = SUBMERGED_DEPTHS.kraken; }
      if (state.timer >= KRAKEN.dreadSeconds) enter(state, 'mantle');
      break;
    }

    case 'mantle': {
      if (!boss) break;
      boss.submerged = false;
      boss.submergedDepth = 0;

      /*
       * Slams on a cycle, and each one leaves the eye open afterwards. That ordering is
       * the phase's whole rhythm: the attack you cannot prevent creates the window you
       * need, so taking a hit is the price of the shot rather than a punishment for
       * being slow.
       */
      state.slamTimer -= dt;
      if (state.slamTimer <= 0) {
        state.slammed = true;
        state.slamTimer = KRAKEN.slamIntervalSeconds;
        state.eyeExposed = true;
        state.timer = 0;
      }
      if (state.eyeExposed && state.timer > KRAKEN.eyeWindowSeconds) {
        state.eyeExposed = false;
      }

      if (state.mantleHits >= KRAKEN.mantleTorpedoHits) {
        state.cycle++;
        if (state.cycle >= KRAKEN.cycles) {
          // The range fight is lost. It stops keeping its distance.
          enter(state, 'tentacles');
          state.tentaclesToSpawn = KRAKEN.tentacleCount;
        } else {
          enter(state, 'submerged');
        }
      }
      break;
    }

    case 'submerged': {
      if (!boss) break;
      boss.submerged = true;
      boss.submergedDepth = SUBMERGED_DEPTHS.kraken;

      // Circling deep. The bulge wake and the hydrophone cone are the only tells.
      const phase = state.timer * 0.12 + state.cycle;
      boss.goalX = Math.sin(phase) * KRAKEN.orbitRadius;
      boss.goalZ = Math.cos(phase) * KRAKEN.orbitRadius;
      boss.speed = vesselSpec('kraken').speed;

      // Enough charges on its track drives it back up, weakened.
      if (state.chargeHits >= KRAKEN.chargesToForceSurface) {
        enter(state, 'mantle');
        break;
      }
      // It will not stay down forever; if the player cannot find it, it comes up anyway.
      if (state.timer >= KRAKEN.submergedSeconds) enter(state, 'mantle');
      break;
    }

    case 'tentacles': {
      if (!boss) break;
      /*
       * The assault. The body slides under and surges for the platform while the limbs
       * come up in front of it; by the time the last limb dies, the mass is already
       * below the structure, which is what lets the head rise from under the line of
       * sight rather than steaming in from the horizon.
       */
      boss.submerged = true;
      boss.submergedDepth = SUBMERGED_DEPTHS.kraken * 0.5;
      const range = Math.max(1, Math.hypot(boss.pos.x, boss.pos.z));
      /*
       * The surge steers into the middle half of the assault window, not down the
       * body's own radial. The limbs root across the platform's open sector, and where
       * the body stops is where the head will rise — measured before this clamp, the
       * submerged orbit had drifted the body to bearing +5 while every limb stood at
       * -78..-10, so the eye came up to the side of a fight the player was already
       * facing away from. Adam: the player should be engaged in exactly that direction
       * just before the eye comes. Held to the window's middle half so the rise always
       * has limbs on both sides of it.
       */
      const windowFrom = (KRAKEN.assaultOpenFromDeg * Math.PI) / 180;
      const windowTo = (KRAKEN.assaultOpenToDeg * Math.PI) / 180;
      const quarter = (windowTo - windowFrom) * 0.25;
      const bearing = Math.max(
        windowFrom + quarter,
        Math.min(windowTo - quarter, Math.atan2(boss.pos.x, boss.pos.z)),
      );
      boss.goalX = Math.sin(bearing) * KRAKEN.stareBodyRange;
      boss.goalZ = Math.cos(bearing) * KRAKEN.stareBodyRange;
      boss.speed = vesselSpec('kraken').speed * 2.6;
      /*
       * No heading override here — `stepVesselMotion` steers toward the goal, and a
       * second hand on the wheel is how the first version of this phase circled at 130 m
       * turn radius forever and the watchdog ended the campaign. Facing the flank to the
       * platform is the rise's job, when the body has stopped.
       */

      const inBand = range >= KRAKEN.riseGateNear && range <= KRAKEN.riseGateFar;
      if (state.tentaclesToSpawn === 0 && tentaclesAlive === 0 && inBand) {
        enter(state, 'rise');
      }
      break;
    }

    case 'rise': {
      if (!boss) break;
      /*
       * The head comes up from under the player's line of sight. The body does not
       * move; the head pitches back until the eye clears the deck line, opens, and
       * looks straight at the stance. `eyeTarget` is the aiming contract — the render
       * places the eye exactly where the simulation counts rounds against.
       */
      boss.submerged = false;
      boss.submergedDepth = 0;
      boss.speed = 0;
      boss.goalX = boss.pos.x;
      boss.goalZ = boss.pos.z;
      boss.heading = turnToward(
        boss.heading, krakenStareHeading(boss.pos.x, boss.pos.z), 0.35 * dt,
      );

      const progress = Math.min(1, state.timer / KRAKEN.riseSeconds);
      state.eyeExposed = progress >= 0.85;
      const eye = krakenEyePoint(
        boss.pos.x, boss.pos.y, boss.pos.z, boss.heading, progress,
      );
      boss.eyeTarget = state.eyeExposed
        ? { x: eye.x, y: eye.y, z: eye.z, r: KRAKEN.eyeTargetRadius }
        : undefined;

      if (state.eyeHits >= KRAKEN.eyeShotsToKill) enter(state, 'dying');
      break;
    }

    case 'dying': {
      if (!boss) break;
      /*
       * The coup de grâce has landed. The eye rolls back, the head falls away from the
       * platform, and the whole mass tumbles under. The body slides beneath the surface
       * partway through the fall so the sea closes over it; the remaining seconds are
       * the silence before the spotter finds a voice.
       */
      boss.speed = 0;
      boss.eyeTarget = undefined;
      const fallStart = KRAKEN.eyeRollSeconds;
      const under = fallStart + KRAKEN.fallSeconds * 0.55;
      if (state.timer >= under) {
        boss.submerged = true;
        boss.submergedDepth = SUBMERGED_DEPTHS.kraken;
      } else {
        boss.submerged = false;
        boss.submergedDepth = 0;
      }
      if (state.timer >= KRAKEN.eyeRollSeconds + KRAKEN.fallSeconds + KRAKEN.silenceSeconds) {
        enter(state, 'gone');
      }
      break;
    }

    case 'gone':
      break;
  }

  // A little jitter so the circling never looks mechanical.
  if (boss && state.phase === 'submerged') {
    boss.goalX += (rng.next() - 0.5) * 40;
    boss.goalZ += (rng.next() - 0.5) * 40;
  }

  rampSubmergence();
}

/**
 * A weapon landed on the mantle. Only counts while it is surfaced.
 *
 * `mantleHits` is a running total in torpedo-equivalents rather than a count of fish, so
 * every weapon the campaign hands out can finish the phase — see `KRAKEN.mantleProgress`
 * for what each is worth and why.
 */
export function registerMantleHit(
  state: KrakenState,
  kind: 'shell' | 'rocket' | 'rpg' | 'torpedo' | 'depthCharge' = 'torpedo',
): void {
  if (state.phase !== 'mantle') return;
  const w = KRAKEN.mantleProgress;
  if (kind === 'torpedo') state.mantleHits += w.torpedo;
  else if (kind === 'depthCharge') state.mantleHits += w.depthCharge;
  else if (kind === 'shell') {
    state.mantleHits += w.shell * (state.eyeExposed ? w.shellEyeMultiplier : 1);
  }
}

/** A depth charge went off near the submerged boss. */
export function registerChargeNear(state: KrakenState, distance: number): void {
  if (state.phase !== 'submerged') return;
  if (distance <= KRAKEN.chargeProximity) state.chargeHits++;
}

/** A shell struck the staring eye. Only the stare counts them. */
export function registerEyeHit(state: KrakenState): void {
  if (state.phase === 'rise' && state.eyeExposed) state.eyeHits++;
}

/**
 * Damage multiplier for a shot landing on the Kraken.
 *
 * The mantle is armoured against everything; the eye is the autocannon's one purchase,
 * and only while it is open. Returning zero rather than a small number is deliberate —
 * a gun that plinks the mantle for 2 damage invites the player to waste a magazine
 * finding out, whereas one that visibly does nothing teaches the lesson immediately.
 * During the stare, eye strikes are counted separately (`registerEyeHit`); the phase
 * machine, not the health pool, decides when the creature dies.
 */
export function krakenDamageFactor(
  state: KrakenState,
  kind: 'shell' | 'rocket' | 'rpg' | 'torpedo' | 'depthCharge',
): number {
  if (kind === 'torpedo') return state.phase === 'mantle' ? 1 : 0;
  if (kind === 'depthCharge') return state.phase === 'submerged' ? 0.5 : 0.15;
  if (kind === 'shell') return state.eyeExposed ? KRAKEN.eyeDamageMultiplier : 0;
  return 0;
}

/**
 * Where an assault limb roots.
 *
 * Close enough to the platform that the reared limb's upper half stands in the pitch
 * band the gun can reach, and — the part that took a screenshot from Adam to find —
 * only on bearings the rig does not mask. The arc is the platform's own open sector
 * (`KRAKEN.assaultOpenFromDeg`/`ToDeg`), not a fan around the creature: a limb behind
 * the Refinery is one the player cannot answer from the only stance they have, which
 * makes severing them all a matter of luck rather than gunnery.
 *
 * The jitter never exceeds half a step, so the ring stays a ring and no two limbs stack.
 */
export function assaultRingPlacement(
  index: number,
  count: number,
  rng: Rng,
): { x: number; z: number; heading: number } {
  const from = (KRAKEN.assaultOpenFromDeg * Math.PI) / 180;
  const to = (KRAKEN.assaultOpenToDeg * Math.PI) / 180;
  const step = count > 1 ? (to - from) / (count - 1) : 0;
  // Clamped, not just jittered: the window's edges are measured masking boundaries, and
  // the end limbs' jitter would otherwise carry them a few degrees behind the structure.
  // Only the two outermost draws are ever affected, and never by a whole step, so the
  // ring cannot collapse.
  const angle = Math.max(from, Math.min(
    to,
    from + index * step + (rng.next() - 0.5) * step * 0.5,
  ));
  const range = KRAKEN.assaultRangeNear
    + rng.next() * (KRAKEN.assaultRangeFar - KRAKEN.assaultRangeNear);
  return {
    x: Math.sin(angle) * range,
    z: Math.cos(angle) * range,
    heading: rng.next() * Math.PI * 2,
  };
}

/**
 * How wide the ring of limbs is around whatever they are surfacing on, metres.
 *
 * Wide enough that they are separate contacts on the radar and separate targets in the
 * scope; tight enough that the sea between them plainly belongs to one animal.
 *
 * And outside grapple reach, which is the number that matters most. A tentacle's weapon
 * range is 90 m, so the old 71–139 m ring had half the limbs already crushing the hull
 * on the frame they surfaced — no window to answer, and Endless watch 10 lost its convoy
 * every time. From here the nearest still has ten seconds of visible closing to do.
 */
const TENTACLE_RING_RADIUS = 190;

/**
 * Place one limb of a group as part of a ring, not a rank.
 *
 * Adam: "they shouldn't appear in one long row — they should be spaced in more of a circle
 * so it looks like they all belong to the same creature that lurks beneath."
 *
 * The scripted spawn beats fan a group along a bearing arc at one constant range, which
 * from a fixed viewpoint a kilometre away is a row of identical posts across the screen —
 * every limb the same distance, and every heading pointing back at the rig, so they also
 * all present the same face. This distributes them around the group's centre instead: each
 * limb gets its own range, its own bearing and its own heading, and the ones on the far
 * side stand behind the ones in front. The depth is what sells a body underneath them.
 *
 * The angle is struck from the index rather than the seed, so the ring stays a ring — the
 * random part only breaks its regularity, and never lets two limbs stack up.
 */
export function tentacleRingPlacement(
  index: number,
  count: number,
  centreX: number,
  centreZ: number,
  rng: Rng,
): { x: number; z: number; heading: number } {
  const step = (Math.PI * 2) / Math.max(count, 1);
  const angle = index * step + (rng.next() - 0.5) * step * 0.55;
  const radius = TENTACLE_RING_RADIUS * (0.8 + rng.next() * 0.4);
  return {
    x: centreX + Math.sin(angle) * radius,
    z: centreZ + Math.cos(angle) * radius,
    // Its own face to the platform. A limb is not a boat and has no reason to be bow-on.
    heading: rng.next() * Math.PI * 2,
  };
}
